import React, { useCallback, useRef } from 'react';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { StyledNuvoBox } from '#/common/styles/template';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import { NuvoLabel } from '@nuvolo/nuux-core/components/NuvoLabel';
import DropZone from './DropZone';
import FileInfoCard from './FileInfoCard';
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export interface FileUploadProps {
  uploadedFile: File | null;
  onFileSelect: (file: File | null) => void;
  acceptedFormats: string[]; // e.g., ['.csv', '.xlsx']
  maxFileSizeBytes?: number; // Maximum file size in bytes
}

const FileUpload: React.FC<FileUploadProps> = ({
  uploadedFile,
  onFileSelect,
  acceptedFormats,
  maxFileSizeBytes = 25 * 1024 * 1024, // Default 25MB
}) => {
  const { isDesktop } = useViewport();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptString = acceptedFormats.join(',');
  const displayFormats = acceptedFormats.map((f) => f.toUpperCase().replace('.', '')).join(', ');

  const handleFileSelect = useCallback(
    (file: File) => {
      // Validate file format
      const fileExtension = '.' + file.name.toLowerCase().split('.').pop();
      if (!acceptedFormats.some((format) => format.toLowerCase() === fileExtension)) {
        alert(`Invalid file format. Only ${displayFormats} files are accepted.`);
        return;
      }

      // Validate file size
      if (file.size > maxFileSizeBytes) {
        alert(`File size exceeds the maximum allowed size of ${formatFileSize(maxFileSizeBytes)}.`);
        return;
      }

      onFileSelect(file);
    },
    [onFileSelect, acceptedFormats, maxFileSizeBytes, displayFormats],
  );

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
      // Reset input value to allow selecting the same file again
      event.target.value = '';
    },
    [handleFileSelect],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      const file = event.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDelete = useCallback(() => {
    onFileSelect(null);
  }, []);

  return (
    <StyledNuvoBox isDesktop={isDesktop}>
      <div className="box-caption">
        <NuvoLabel>Import File</NuvoLabel>
      </div>
      <NuvoBox display="flex" flexDirection="column" padding={24} id="tmm-section">
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptString}
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />

        {!uploadedFile ? (
          <DropZone displayFormats={displayFormats} onDrop={handleDrop} onClick={handleClick} />
        ) : (
          <FileInfoCard
            fileName={uploadedFile.name}
            fileSize={uploadedFile.size}
            onDelete={handleDelete}
          />
        )}
      </NuvoBox>
    </StyledNuvoBox>
  );
};

export default React.memo(FileUpload);
